registerComponent("product-blocks/product-profitability", function (component) {
/**
 * O corpo recebe o elemento raiz do componente em `component`; o build o
 * envolve em registerComponent(). Os antigos `params` viraram @props no Blade.
 *
 * O gráfico de rentabilidade. Em 2020 as 200 linhas abaixo eram JS INLINE,
 * escritas pelo PHP dentro de `@push('scripts')` em
 * `site/template/graphic-data.blade.php` — com os pontos das duas séries
 * emitidos por dois `@foreach`. Agora o Blade emite só os DADOS, num
 * `<script type="application/json" data-grafico-rentabilidade>`, e o desenho
 * é este arquivo. O `@php` que escolhe série/`charts`/`profit`, o `@push('head')`
 * com o CSS da `.floating-tooltip-2` e o `@pushOnce` do `charts.min.js` ficam
 * onde estavam.
 *
 * ⚠️ ORDEM. `charts.min.js` entra pelo `@stack('scripts')`, DEPOIS dos quatro
 * bundles do layout, e é `defer` como eles: script adiado roda na ordem do
 * documento, e o `DOMContentLoaded` — que é quando o runtime de componentes
 * boota — só vem depois de todos. Por isso aqui `LightweightCharts` já existe.
 * Um boot antecipado (`readyState === 'interactive'`) o veria `undefined`, e a
 * guarda abaixo é o que o `browser-check` lê: sem a biblioteca, `#chart` leva
 * `data-grafico="vazio"` e o portão acusa, em vez de a página ficar sem gráfico
 * em silêncio.
 *
 * O JSON está FORA da raiz do componente (o `@push` deságua no fim do body),
 * então a busca é no documento.
 *
 * ⚠️ AS SÉRIES NÃO VÊM NO HTML (21/09, PERF-010). O `<script type="application/json">`
 * traz só os rótulos do idioma; os 2 × 2.832 pontos vêm de
 * `/api/fundos/{slug}/grafico`, no `data-url` dele — eram 199 KB dentro do
 * documento, que responde `no-cache`. O gráfico é criado na hora, com as duas
 * séries vazias, e os pontos entram quando a resposta chega: o `<div>` de 250px
 * já está no fluxo, então nada se desloca por isso.
 *
 * ⚠️ O FORMATO DA RESPOSTA É COLUNAR (`dates`, `fund`, `benchmark`, arrays
 * paralelos): a data saía duas vezes por ponto, uma em cada série. Quem remonta
 * o `{ time, value }` que o lightweight-charts espera é o `paraSerie` abaixo.
 */

const chartEl = component.querySelector('#chart');
const dadosEl = document.querySelector('[data-grafico-rentabilidade]');

if (chartEl && dadosEl) {
    if (typeof LightweightCharts === 'undefined') {
        console.error('product-profitability: LightweightCharts não carregou; o gráfico não foi desenhado.');
        chartEl.dataset.grafico = 'vazio';
    } else {
        const dados = JSON.parse(dadosEl.textContent);

        // O `width: 100%` / `height: 250px` que o `graphic-data.blade.php`
        // escrevia em `container.style`, à letra. A altura virou constante
        // porque o redesenho no `resize` a repassa ao gráfico.
        const ALTURA_GRAFICO = 250;
        const container = document.createElement('div');

        container.style.width = '100%';
        container.style.height = `${ALTURA_GRAFICO}px`;
        chartEl.appendChild(container);

        const chart = LightweightCharts.createChart(container, {
            localization: {
                locale: dados.locale,
                dateFormat: 'MMMM/yyyy',
                priceFormatter: (price) => new Intl.NumberFormat('pt-BR').format(price) + '%',
            },

            layout: {
                backgroundColor: 'transparent',
                textColor: '#BEBEBE',
                fontFamily: 'Montserrat,Arial,Helvetica,sans-serif',
                fontSize: 12,
            },

            grid: {
                horzLines: { color: '#c0b8b3', style: 3 },
                vertLines: { color: '#ddd' },
            },

            crosshair: {
                vertLine: {
                    color: '#96845c',
                    width: 0.5,
                    style: 1,
                    visible: true,
                    labelVisible: true,
                },
                horzLine: {
                    color: '#96845c',
                    width: 0.5,
                    style: 1,
                    visible: false,
                    labelVisible: false,
                },
            },

            priceScale: {
                position: 'left',
                mode: 0,
                autoScale: true,
                alignLabels: true,
                borderVisible: false,
                borderColor: '#bbae82',
                borderWidth: 3,
                drawTicks: true,
            },

            timeScale: {
                fixRightEdge: true,
                fixLeftEdge: true,
                lockVisibleTimeRangeOnResize: true,
                rightBarStaysOnScroll: true,
                borderVisible: false,
                borderColor: '#bbae82',
                visible: true,
                timeVisible: false,
                secondsVisible: false,
                minBarSpacing: 0.001,
                tickMarkFormatter: (time) => String(
                    LightweightCharts.isBusinessDay(time)
                        ? dados.months[time.month - 1] + '/' + String(time.year).substring(2, 4)
                        : new Date(time * 1000).getUTCFullYear(),
                ),
            },

            handleScroll: true,
            handleScale: true,
        });

        const fundLineSeries = chart.addLineSeries({
            color: '#84754d',
            lineWidth: 2,
            lineStyle: 0,
            crosshairMarkerVisible: true,
            crosshairMarkerRadius: 3,
        });


        const cdiLineSeries = chart.addLineSeries({
            color: '#8b9091',
            lineWidth: 2,
            lineStyle: 0,
            crosshairMarkerVisible: true,
            crosshairMarkerRadius: 3,
        });

        /*
         | Os pontos, do endpoint. `fitContent()` só depois de `setData`: sem
         | dado nenhum ele não teria intervalo que encaixar.
         |
         | Falha de rede não pode derrubar o resto do bloco (a tabela de
         | rentabilidade, que é HTML): o gráfico fica vazio e marcado, que é o
         | mesmo estado que o `browser-check` já lê quando a biblioteca não vem.
         */
        const paraSerie = (dados, valores) => valores.map((value, i) => ({ time: dados.dates[i], value }));

        fetch(dadosEl.dataset.url, { headers: { Accept: 'application/json' } })
            .then((resposta) => {
                if (!resposta.ok) throw new Error(`HTTP ${resposta.status}`);

                return resposta.json();
            })
            .then((series) => {
                fundLineSeries.setData(paraSerie(series, series.fund));
                cdiLineSeries.setData(paraSerie(series, series.benchmark));
                chart.timeScale().fitContent();
            })
            .catch((erro) => {
                console.error('product-profitability: as séries do gráfico não vieram.', erro);
                chartEl.dataset.grafico = 'vazio';
            });

        /*
         | O gráfico acompanha a largura da janela.
         |
         | O lightweight-charts 3.8 dimensiona o canvas UMA vez, na criação, a
         | partir da caixa do contêiner: o `width: 100%` do `<div>` continua
         | encolhendo, mas o desenho dentro dele não — e a página só se
         | ajeitava no F5. (O `autoSize` que resolveria isso sozinho só existe
         | da v4 em diante.)
         |
         | `ResizeObserver` em vez de `resize` da janela porque o que importa é
         | a caixa do bloco, que muda também quando o submenu prende ou a barra
         | de rolagem aparece. `fitContent()` refaz o encaixe do intervalo, que
         | `lockVisibleTimeRangeOnResize` preserva na largura antiga.
         */
        const redesenhar = () => {
            const largura = container.clientWidth;

            if (largura > 0) {
                chart.resize(largura, ALTURA_GRAFICO);
                chart.timeScale().fitContent();
            }
        };

        if (typeof ResizeObserver === 'function') {
            new ResizeObserver(redesenhar).observe(chartEl);
        } else {
            window.addEventListener('resize', redesenhar);
        }

        // ── A tooltip ──────────────────────────────────────────────────────
        // `dateFormat` é `d/m/Y` em pt e `m/d/Y` em en: era o `@if($lang)` que
        // trocava a ordem dentro do `businessDayToString`.
        const dataEmTexto = (businessDay) => (dados.dateFormat === 'm/d/Y'
            ? businessDay.month + '/' + businessDay.day + '/' + businessDay.year
            : businessDay.day + '/' + businessDay.month + '/' + businessDay.year);

        const LARGURA_TOOLTIP = 100;
        const ALTURA_TOOLTIP = 80;
        const MARGEM_TOOLTIP = 15;

        const toolTip = document.createElement('div');

        toolTip.className = 'floating-tooltip-2';
        container.appendChild(toolTip);

        chart.subscribeCrosshairMove(function (param) {
            // Tagueamento (plano v3.1): a primeira interação com o gráfico.
            if (param.point !== undefined && param.time) Asa.trackUmaVez('grafico', 'chart_interaction', {});

            if (param.point === undefined
                || !param.time
                || param.point.x < 0
                || param.point.x > container.clientWidth
                || param.point.y < 0
                || param.point.y > container.clientHeight) {
                toolTip.style.display = 'none';

                return;
            }

            const dateStr = dataEmTexto(param.time);

            toolTip.style.display = 'block';

            const priceFund = param.seriesPrices.get(fundLineSeries);
            const priceCDI = param.seriesPrices.get(cdiLineSeries);
            const fundPriceString = String(Math.round(priceFund * 100) / 100) + '%';
            const CDIPriceString = String(Math.round(priceCDI * 100) / 100) + '%';

            // O rótulo do benchmark é o `label_2` da aba Rentabilidade do CMS:
            // entra escapado, senão um `<img onerror>` ali rodava ao passar o
            // mouse no gráfico (achado A1 do QA de produtos, 13/10/2026).
            const escapar = (valor) => String(valor ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

            toolTip.innerHTML = `
                    <div style="color: #96845c;">${escapar(dados.labelFundo)}</div>
                        <div style="font-size: 18px; margin: 4px 0px">${fundPriceString.replace('.', ',')}</div>
                    <div>${dateStr}</div>
                    <hr>
                    <div style="color: #4f4d4f;">${escapar(dados.labelBenchmark)}</div>
                        <div style="font-size: 18px; margin: 4px 0px">${CDIPriceString.replace('.', ',')}</div>
                    <div>${dateStr}</div>
                `;

            const coordinate = fundLineSeries.priceToCoordinate(priceFund);

            if (coordinate === null) {
                return;
            }

            const shiftedCoordinate = Math.max(
                0,
                Math.min(container.clientWidth - LARGURA_TOOLTIP, param.point.x - 50),
            );
            const coordinateY = coordinate - ALTURA_TOOLTIP - MARGEM_TOOLTIP > 0
                ? coordinate - ALTURA_TOOLTIP - MARGEM_TOOLTIP
                : Math.max(
                    0,
                    Math.min(container.clientHeight - ALTURA_TOOLTIP - MARGEM_TOOLTIP, coordinate + MARGEM_TOOLTIP),
                );

            toolTip.style.left = shiftedCoordinate + 'px';
            toolTip.style.top = coordinateY + 'px';
        });
    }
}

});
